import React, { useEffect, useState } from 'react'
import { AutoComplete } from '@/components/ui/autocomplete'
import { Button } from '@/components/ui/button'
import { CirclePlus, Minus, User } from 'lucide-react'
import { useDebounce } from '@uidotdev/usehooks'
import { Option } from '@/components/ui/autocomplete'
import getPharmacyID from '@/utils/pharmacy_code'
import { httpClient } from '@/httpClient'
import { Patient, PatientMinimal } from '@/routes/(pharma)/_layout/patients/-data/schema'
import { toast } from '@/components/ui/use-toast'
import { useQuery } from '@connectrpc/connect-query'
import { listPatients } from '@/gen/pharmacy-PharmacyService_connectquery'
import { pharmacyClient } from '@/connect'

const PatientInfo = ({
    head,
    value
}: {
    head: string,
    value: string
}) => {
    return (
        <div>
            <p className='font-light text-sm'>{head}</p>
            <p className='font-semibold text-sm'>{value}</p>
        </div>
    )
}

const PatientInfoInput = ({
    head,
    value,
    onChange
}: {
    head: string,
    value: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}) => {
    return (
        <div>
            <p className='text-sm font-semibold mt-2'>{head}<span className='text-red-400'>*</span></p>
            <input
                type='text'
                className='border-[0.5px] rounded-lg p-2 text-sm w-full'
                value={value}
                onChange={onChange}
            />
        </div>
    )
}

enum PatientState {
    Closed = 'closed',
    Open = 'open',
    Added = 'added',
}

const API_URL = import.meta.env.VITE_API_URL as string

const AttachPatient = ({patientInfo, setPatientInfo}: {patientInfo: PatientMinimal | undefined, setPatientInfo: React.Dispatch<React.SetStateAction<PatientMinimal | undefined>>}) => {
    const [patientName, setPatientName] = useState<string>('')
    const [patientPhone, setPatientPhone] = useState<string>('')
    const [patientGovtId, setPatientGovtId] = useState<string>('')
    const [createPatientState, setCreatePatientState] = useState<PatientState>(PatientState.Closed)

    const [searchPatient, setSearchPatient] = useState<string>('')
    const [selectedPatient, setSelectedPatient] = useState<Option>()
    const [loading, setLoading] = useState<boolean>(false)

    const debouncedSearch = useDebounce(searchPatient, 500)

    const {data, isLoading} = useQuery(listPatients, {
        searchQuery: debouncedSearch,
        page: 1,
        pageSize: 10,
    })

    useEffect(() => {
        if (selectedPatient) {
            setPatientGovtId(selectedPatient.label.split(' | ')[2])
            setPatientName(selectedPatient.label.split(' | ')[0])
            setPatientPhone(selectedPatient.label.split(' | ')[1])
            setCreatePatientState(PatientState.Added)
            setPatientInfo({
                id: selectedPatient.value,
                name: selectedPatient.label.split(' | ')[0],
                phone: selectedPatient.label.split(' | ')[1],
                govt_identifier: selectedPatient.label.split(' | ')[2]
            })
        }
    }, [selectedPatient])
    

    return (
        <div className='px-4 min-h-[300px] w-[97%] grid place-items-center'>
            <div className="p-4 bg-gray-100 h-full rounded-lg w-full">
                <div className='flex items-center justify-between'>
                    <div>
                        <p className="font-semibold">Attach a patient <span className='font-normal'>(Optional)</span></p>
                        <p className="text-gray-500">
                            Select a patient or create one
                        </p>
                    </div>                    
                    {
                        (createPatientState === PatientState.Added) && (
                            <Button
                                variant="outline"
                                className='bg-white text-black'
                                onClick={() => {
                                    setCreatePatientState(PatientState.Closed)
                                    setPatientName('')
                                    setPatientPhone('')
                                    setPatientGovtId('')
                                    setSelectedPatient(undefined)
                                    setPatientInfo(undefined)
                                    setSearchPatient('')
                                }}
                            >
                                Remove Patient
                            </Button>
                        )
                    }
                    {
                        (createPatientState === PatientState.Open) && (
                            <Button
                                variant="outline"
                                className='bg-white text-black'
                                onClick={() => setCreatePatientState(PatientState.Closed)}
                            >
                                <Minus className='w-4 h-4 mr-2' />
                                Cancel
                            </Button>
                        )
                    }
                </div>
                <hr className='my-2' />
                {
                    (createPatientState === PatientState.Closed) && (
                        <AutoComplete 
                            options={data?.patients.map(patient => {
                                return {
                                    value: patient.patientId,
                                    label: `${patient.name} | ${patient.phone} | ${patient.govtIdentifier}`
                                }
                            }) || []}
                            emptyMessage="No results."
                            placeholder="Search..."
                            onValueChange={setSelectedPatient}
                            value={selectedPatient}
                            input={searchPatient}
                            onInputChanged={setSearchPatient}
                            isLoading={isLoading || loading}
                        />
                    )                    
                }

               {
                    (createPatientState === PatientState.Added) && (
                        <div className='flex flex-col space-y-2 p-2 rounded-lg border-[0.5px] bg-white'>
                        <PatientInfo head='Name' value={patientName} />
                        <PatientInfo head='Phone Number' value={patientPhone} />
                        <PatientInfo head='Govt. Identifier' value={patientGovtId} />
                        </div>
                    )
               }

                {
                    (createPatientState === PatientState.Open) && (
                        <div className='flex flex-col space-y-2 p-2 rounded-lg border-[0.5px] bg-white'>
                            <PatientInfoInput head='Name' value={patientName} onChange={(e) => setPatientName(e.target.value)} />
                            <PatientInfoInput head='Phone Number' value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} />
                            <PatientInfoInput head='Govt. Identifier' value={patientGovtId} onChange={(e) => setPatientGovtId(e.target.value)} />
                            <Button
                                variant="outline"
                                className='text-blue-700 border-[0.5px] border-blue-700 bg-white'
                                disabled={loading}
                                onClick={async () => {
                                    try {
                                        setLoading(true);
                                        if (!patientName || !patientPhone || !patientGovtId) {
                                            toast({
                                                title: "Error",
                                                variant: "destructive",
                                                description: "Please fill all the fields",
                                                duration: 5000,
                                            });
                                            setLoading(false);
                                            return;
                                        }
                                        const response = await pharmacyClient.createPatient({
                                            name: patientName,
                                            phone: patientPhone,
                                            govtIdentifier: patientGovtId,
                                        })

                                        toast({
                                            title: "New Patient Added",
                                            variant: "success",
                                            description: `Patient ${patientName} has been added successfully`,
                                            duration: 5000,
                                        });

                                        setCreatePatientState(PatientState.Added)
                                        setPatientInfo({
                                            id: response.patientId,
                                            name: patientName,
                                            phone: patientPhone,
                                            govt_identifier: patientGovtId
                                        })
                                        setLoading(false);
                                    } catch (error) {
                                        console.error('Error:', error);
                                        toast({
                                            title: "Error",
                                            variant: "destructive",
                                            description: "Failed to add patient. Please try again later.",
                                            duration: 5000,
                                        });
                                        setLoading(false);
                                    }
                                }}
                            >
                                <User className='w-4 h-4 mr-2' />
                                Create Patient
                            </Button>
                        </div>
                    )
                }

                {
                    (createPatientState === PatientState.Closed) && (
                        <Button
                            variant="outline"
                            className='w-full bg-white text-black text-sm my-24'
                            onClick={() => setCreatePatientState(PatientState.Open)}
                        >
                            <CirclePlus className='w-4 h-4 mr-2' />
                            Create New Patient
                        </Button>
                    )
                }
            </div>
        </div>
    )
}

export default AttachPatient